import React from 'react';
import _ from 'lodash';
import {connect} from 'react-redux';

import DebouncedInput from 'components/DebouncedInput';
import Select from 'components/Select';
import Button from 'components/Button';
import LabelV2 from 'components/LabelV2';
import DateInput from 'components/DateInput';
import VisibleIf from 'components/VisibleIf';
import ErrorBlock from 'components/ErrorBlock';
import {default as Modal, withModal} from 'components/Modal';

import {storeDataInModal} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {
  MODALS,
  fetchAllocations,
  fetchOfflinePeriod,
  editOfflinePeriodEndDate,
  editOfflinePeriodStartDate,
  fetchOfflinePeriodReasons,
  editOfflinePeriodReason,
  editOfflinePeriodNotes
} from 'ducks/allocations';

import {headOption} from 'utils/common';

import {formatDate, formatDateForApi} from 'utils/datetime';

import './styles.css';

const Element = ({label, value}) => (
  <div className="Element">
    <LabelV2>{label}</LabelV2>
    <div className="text">{value}</div>
  </div>
);

const buildOfflinePeriodReasonOption = reason => ({
  label: reason.title,
  value: reason.id
});

class OfflinePeriodDetailModal extends React.Component<any> {
  componentDidMount() {
    this.props.fetchOfflinePeriod(
      {
        offlinePeriodId: this.props.data.offlinePeriodId
      },
      this.props.storeDataExtraActions(data => ({offlinePeriod: data}))
    );

    this.props.fetchOfflinePeriodReasons(
      this.props.storeDataExtraActions(data => ({offlinePeriodReasons: data}))
    );
  }

  getEditExtraActionsForField = () => {
    const {offlinePeriodId} = this.props.data;

    const fetchOfflinePeriodExtraActions = [
      data =>
        storeDataInModal({
          modalName: MODALS.OFFLINE_PERIOD_DETAIL,
          data: {
            offlinePeriod: data
          }
        })
    ];

    const extraActions = [
      () =>
        fetchOfflinePeriod({offlinePeriodId}, fetchOfflinePeriodExtraActions),
      () =>
        storeDataInModal({
          modalName: MODALS.OFFLINE_PERIOD_DETAIL,
          data: {
            submitError: null
          }
        })
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: MODALS.OFFLINE_PERIOD_DETAIL,
          data: {
            submitError: _.get(errors, '_error', null)
          }
        })
    ];

    return {extraActions, failureExtraActions};
  };

  onOfflinePeriodStartDateChange = newStartDate => {
    const {offlinePeriodId, locationRef} = this.props.data;

    const {
      extraActions,
      failureExtraActions
    } = this.getEditExtraActionsForField();

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD,
        locationRef
      },
      () => {
        this.props.editOfflinePeriodStartDate(
          {
            offlinePeriodId,
            newStartDate: formatDateForApi(newStartDate)
          },
          extraActions,
          failureExtraActions
        );
      }
    );
  };

  onOfflinePeriodEndDateChange = newEndDate => {
    const {offlinePeriodId, locationRef} = this.props.data;

    const {
      extraActions,
      failureExtraActions
    } = this.getEditExtraActionsForField();

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD,
        locationRef
      },
      () => {
        this.props.editOfflinePeriodEndDate(
          {
            offlinePeriodId,
            newEndDate: formatDateForApi(newEndDate)
          },
          extraActions,
          failureExtraActions
        );
      }
    );
  };

  onOfflinePeriodReasonChange = newOfflinePeriodReasonOption => {
    const {offlinePeriodId, locationRef} = this.props.data;

    const {
      extraActions,
      failureExtraActions
    } = this.getEditExtraActionsForField();

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD,
        locationRef
      },
      () => {
        this.props.editOfflinePeriodReason(
          {
            offlinePeriodId,
            newOfflinePeriodReasonId: newOfflinePeriodReasonOption.value
          },
          extraActions,
          failureExtraActions
        );
      }
    );
  };

  onOfflinePeriodNotesChange = newOfflinePeriodNotes => {
    const {offlinePeriodId, locationRef} = this.props.data;

    const {
      extraActions,
      failureExtraActions
    } = this.getEditExtraActionsForField();

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD,
        locationRef
      },
      () => {
        this.props.editOfflinePeriodNotes(
          {
            offlinePeriodId,
            newOfflinePeriodNotes
          },
          extraActions,
          failureExtraActions
        );
      }
    );
  };

  closeModal = () => {
    const {locationRef} = this.props.data;

    this.props.fetchAllocations({locationRef});
    this.props.closeModal();
  };

  render() {
    const {
      data: {
        offlinePeriod,
        room,
        locationRef,
        submitError,
        offlinePeriodReasons = []
      }
    } = this.props;

    if (!offlinePeriod) {
      return null;
    }

    const offlinePeriodReasonsOptions = offlinePeriodReasons.map(
      buildOfflinePeriodReasonOption
    );
    const offlinePeriodReason = headOption(
      offlinePeriodReasonsOptions.filter(
        reason => reason.label === offlinePeriod.reason
      )
    );
    return (
      <Modal
        modalOverlayClassName="OfflinePeriodDetailModalOverlay"
        className="OfflinePeriodDetailModal"
        show={true}
        type={Modal.EXTRA_SMALL}
        onClose={this.closeModal}>
        <Modal.Header heading="Out of Order Period" />
        <Modal.Body>
          {submitError && <ErrorBlock>{submitError}</ErrorBlock>}
          <div className="OfflinePeriodDetailModal__mainInformation colab-row-space-between">
            <div className="colab-row-space-between">
              <Element label="Room Number" value={room.roomNumber} />
              <Element label="Created by" value={offlinePeriod.createdBy} />
            </div>
            <VisibleIf
              permission={PERMISSIONS.CREATE_DELETE_OFFLINE_PERIOD}
              locationRef={locationRef}>
              <Button
                red
                onClick={() =>
                  this.props.openDeleteOfflinePeriodModal({
                    offlinePeriodId: offlinePeriod.id,
                    roomNumber: room.roomNumber
                  })
                }>
                Delete
              </Button>
            </VisibleIf>
          </div>
          <div className="content">
            <div className="sectionContainer colab-row-space-between">
              <div>
                <LabelV2>Start date</LabelV2>
                <DateInput
                  onChange={this.onOfflinePeriodStartDateChange}
                  value={
                    offlinePeriod.startDate
                      ? formatDate(offlinePeriod.startDate)
                      : undefined
                  }
                />
              </div>
              <div>
                <LabelV2>End date</LabelV2>
                <DateInput
                  onChange={this.onOfflinePeriodEndDateChange}
                  value={
                    offlinePeriod.endDate
                      ? formatDate(offlinePeriod.endDate)
                      : undefined
                  }
                />
              </div>
            </div>
            <div className="sectionContainer">
              <LabelV2>Reason</LabelV2>
              <Select
                options={offlinePeriodReasonsOptions}
                value={offlinePeriodReason}
                onChange={this.onOfflinePeriodReasonChange}
                clearable={false}
              />
            </div>
            <div className="sectionContainer">
              <LabelV2>Details</LabelV2>

              <DebouncedInput
                value={offlinePeriod.notes}
                onChange={this.onOfflinePeriodNotesChange}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

const mapDispatchToProps = {
  fetchOfflinePeriod,
  editOfflinePeriodStartDate,
  editOfflinePeriodEndDate,
  requiresPermission,
  storeDataInModal,
  fetchAllocations,
  fetchOfflinePeriodReasons,
  editOfflinePeriodReason,
  editOfflinePeriodNotes
};

export default connect(null, mapDispatchToProps)(
  withModal(MODALS.OFFLINE_PERIOD_DETAIL)(OfflinePeriodDetailModal)
);
